import React, { useState, useMemo, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { usePermissions } from '@/hooks/usePermissions'
import {
  LayoutDashboard,
  Building2,
  Users,
  BarChart3,
  CreditCard,
  Settings,
  HelpCircle,
  FileText,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Activity,
  DollarSign,
  Package,
  AlertCircle,
  Zap,
  Folder,
  Tag,
  Calendar,
  Layout,
  Network,
} from 'lucide-react'

interface SidebarProps {
  collapsed?: boolean
  onToggleCollapse?: () => void
}

interface NavItem {
  title: string
  href: string
  icon: React.ReactNode
  badge?: string | number
  children?: NavItem[]
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onToggleCollapse
}) => {
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const { hasPermission, isLoading } = usePermissions()

  /**
   * Filter navigation items based on user permissions
   * Marketers see only Blog section, others see everything they have access to
   */
  const navItems = useMemo(() => {
    const allNavItems: NavItem[] = [
      {
        title: 'Dashboard',
        href: '/',
        icon: <LayoutDashboard className="h-5 w-5" />,
      },
      {
        title: 'Tenants',
        href: '/tenants',
        icon: <Building2 className="h-5 w-5" />,
        children: [
          {
            title: 'All Tenants',
            href: '/tenants',
            icon: <Building2 className="h-4 w-4" />,
          },
          {
            // SUB-3.13 / FR62. Hiding this entry is not a control: the gate is
            // is_superadmin() inside list_tenant_hierarchy().
            title: 'Hierarchy',
            href: '/tenants/hierarchy',
            icon: <Network className="h-4 w-4" />,
          },
        ],
      },
      {
        title: 'Users',
        href: '/users',
        icon: <Users className="h-5 w-5" />,
      },
      {
        title: 'Articles',
        href: '/blog',
        icon: <FileText className="h-5 w-5" />,
        children: [
          {
            title: 'Posts',
            href: '/blog/posts',
            icon: <FileText className="h-4 w-4" />,
          },
          {
            title: 'Categories',
            href: '/blog/categories',
            icon: <Folder className="h-4 w-4" />,
          },
          {
            title: 'Tags',
            href: '/blog/tags',
            icon: <Tag className="h-4 w-4" />,
          },
          {
            title: 'Templates',
            href: '/blog/templates',
            icon: <Layout className="h-4 w-4" />,
          },
          {
            title: 'Calendar',
            href: '/blog/calendar',
            icon: <Calendar className="h-4 w-4" />,
          },
        ],
      },
      {
        title: 'Analytics',
        href: '/analytics',
        icon: <BarChart3 className="h-5 w-5" />,
        children: [
          {
            title: 'Overview',
            href: '/analytics',
            icon: <Activity className="h-4 w-4" />,
          },
          {
            title: 'Revenue',
            href: '/analytics/revenue',
            icon: <DollarSign className="h-4 w-4" />,
          },
          {
            title: 'Usage',
            href: '/analytics/usage',
            icon: <Zap className="h-4 w-4" />,
          },
        ],
      },
      {
        title: 'Billing',
        href: '/billing',
        icon: <CreditCard className="h-5 w-5" />,
        children: [
          {
            title: 'Subscriptions',
            href: '/billing/subscriptions',
            icon: <Package className="h-4 w-4" />,
          },
          {
            title: 'Invoices',
            href: '/billing/invoices',
            icon: <FileText className="h-4 w-4" />,
          },
        ],
      },
      {
        title: 'Support',
        href: '/support',
        icon: <HelpCircle className="h-5 w-5" />,
      },
      {
        title: 'Security',
        href: '/security',
        icon: <Shield className="h-5 w-5" />,
        children: [
          {
            title: 'Audit Logs',
            href: '/security/audit',
            icon: <FileText className="h-4 w-4" />,
          },
          {
            title: 'Alerts',
            href: '/security/alerts',
            icon: <AlertCircle className="h-4 w-4" />,
          },
        ],
      },
      {
        title: 'Settings',
        href: '/settings',
        icon: <Settings className="h-5 w-5" />,
      },
    ]

    if (isLoading) return []

    // Super admins see everything
    if (hasPermission('system.override')) {
      return allNavItems
    }

    // Check if user is a Marketer (has blog permissions but not system.override)
    const hasBlogPermissions = hasPermission('view_blog_posts')

    if (hasBlogPermissions && !hasPermission('system.override')) {
      // Marketer: show ONLY blog section
      return allNavItems.filter(item => item.title === 'Articles')
    }

    // For other roles, filter based on their permissions (future-proof)
    // For now, if they don't have blog permissions, show nothing (shouldn't happen)
    return []
  }, [hasPermission, isLoading])

  /**
   * Auto-expand Blog menu for Marketers on mount
   */
  useEffect(() => {
    if (isLoading) return

    const hasBlogPermissions = hasPermission('view_blog_posts')
    const isSuperAdmin = hasPermission('system.override')

    // If user is a Marketer (has blog perms but not super admin), auto-expand Articles menu
    if (hasBlogPermissions && !isSuperAdmin) {
      setExpandedItems(prev => {
        // Only add 'Articles' if it's not already expanded
        if (!prev.includes('Articles')) {
          return [...prev, 'Articles']
        }
        return prev
      })
    }
  }, [hasPermission, isLoading])

  // Child links use an exact match so a parent-index route (e.g. Analytics
  // Overview at /analytics) doesn't stay highlighted on sibling pages.
  const isActive = (href: string, exact = false) => {
    if (exact) return location.pathname === href
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    )
  }

  const renderNavItem = (item: NavItem, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.title)
    const active = isActive(item.href, isChild)

    if (hasChildren && !collapsed) {
      return (
        <div key={item.title}>
          <button
            onClick={() => toggleExpanded(item.title)}
            className={cn(
              'w-full flex items-center rounded-md text-sm font-medium transition-colors',
              isChild ? 'px-6 py-1.5' : 'px-3 py-2',
              active
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted hover:text-foreground text-muted-foreground'
            )}
          >
            <span className="mr-3">{item.icon}</span>
            <span className="flex-1 text-left">{item.title}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                isExpanded ? 'rotate-180' : ''
              )}
            />
          </button>
          {isExpanded && item.children && (
            <div className="mt-1 space-y-1">
              {item.children.map(child => renderNavItem(child, true))}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.href}
        to={item.href}
        className={cn(
          'flex items-center rounded-md text-sm font-medium transition-colors relative',
          isChild ? 'px-6 py-1.5' : 'px-3 py-2',
          collapsed ? 'justify-center' : '',
          active
            ? 'bg-primary text-primary-foreground'
            : 'hover:bg-muted hover:text-foreground text-muted-foreground'
        )}
        title={collapsed ? item.title : undefined}
      >
        <span className={cn('flex-shrink-0', collapsed ? '' : 'mr-3')}>
          {item.icon}
        </span>
        {!collapsed && (
          <>
            <span className="flex-1">{item.title}</span>
            {item.badge && (
              <span className="ml-auto bg-destructive text-destructive-foreground text-xs px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </>
        )}
      </Link>
    )
  }

  return (
    <aside
      className={cn(
        'h-full bg-card border-r border-border transition-all duration-300 flex flex-col',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Sidebar content */}
      <div className="flex-1 overflow-y-auto p-4">
        <nav className="space-y-1">
          {navItems.map(item => renderNavItem(item))}
        </nav>
      </div>

      {/* Collapse toggle */}
      <div className="border-t border-border p-4">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center p-2 hover:bg-muted rounded-md transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>
    </aside>
  )
}